/**
 * AuthStore — Pinia store quản lý toàn bộ auth state (user, token, provider, merchant)
 * Thay thế Vuex moduleContext cho việc lưu accessToken.
 * State được cache qua MISA Cache (@misa/ui-base useCache) để không mất data khi refresh page.
 * CreatedBy: dbhuan (09/06/2026)
 * UpdatedBy: dbhuan (10/06/2026) — them accessToken, refreshToken, setTokens, authorizationHeader
 * UpdatedBy: dbhuan (14/06/2026) — thay hard-coded strings bang ProviderType, StorageKey constants
 * UpdatedBy: dbhuan (16/06/2026) — them OIDC external logout redirect vao logout() nhu logoff cua module-context-actions
 * UpdatedBy: dbhuan (16/06/2026) — inline getAuthConfig tu commonFunction vao AuthStore, bo phu thuoc commonFn
 * UpdatedBy: dbhuan (16/06/2026) — getAuthConfig dung useCache thay localStorage
 * UpdatedBy: dbhuan (16/06/2026) — thay hard-coded "MISAID_V2" bang ProviderType.AUTH_MISA_ID
 * UpdatedBy: dbhuan (16/06/2026) — init() + isMisaIdLogin nhan dien them ProviderType.AUTH_MISA_ID
 * UpdatedBy: dbhuan (16/06/2026) — them roles state + loadRoles() + canShowSettings getter
 */
import { defineStore } from "pinia";
import { useCache } from "@misa/ui-base";
import { CacheKeys, ProviderType, StorageKey, SETTINGS_ROLES } from "@/constants";
import vuexStore from "@/stores/store";
import { useLoadingStore } from "@/stores/LoadingStore";
import { ModuleAuth, ModuleContext } from "@/stores/module-const";
import { RouteName } from "@/routers/routeNames";
import httpClient from "@/apis/base/axios-httpclient";
import Socket from "@/sockets/socket.js";

const { getCache } = useCache();

/**
 * Danh sách localStorage keys cần xóa khi logout
 */
const STORAGE_KEYS_TO_REMOVE = [
  StorageKey.JP_TOKEN,
  StorageKey.JP_USER,
  StorageKey.JP_MERCHANT_STATUS,
  StorageKey.JP_CURRENT_MERCHANT,
  StorageKey.JP_AMPL_SID,
  StorageKey.MS_CURRENT_USER,
  StorageKey.ROLE_LOCAL,
  StorageKey.LOGIN_PROVIDER,
  StorageKey.MENU_COLLAPSE,
  StorageKey.JP_SOCKET_TOKEN,
];

/**
 * Xóa toàn bộ dữ liệu session khỏi localStorage + sessionStorage
 */
function clearSessionStorage() {
  STORAGE_KEYS_TO_REMOVE.forEach((key) => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* silent */
    }
  });
  try {
    window.sessionStorage.clear();
  } catch {
    /* silent */
  }
}

/**
 * Lấy config xác thực từ Identity Provider.
 * Cache qua MISA Cache để tránh gọi API lặp lại.
 * @param {string} idType - ProviderType.AUTH_MISA_ID hoặc ProviderType.AUTH_JETPAY_ID
 * @returns {Promise<Object>} Auth config { authority, client_id, redirect_uri, ... }
 */
async function getAuthConfig(idType) {
  const cache = getCache();
  const cacheKey =
    idType === ProviderType.AUTH_MISA_ID ? CacheKeys.MISA_AUTH_CONFIG : CacheKeys.AUTH_CONFIG;

  const cacheConfig = await cache.get(cacheKey);
  if (cacheConfig) {
    return cacheConfig;
  }

  const url = [window._apis.Auth, "/api/account/config"].join("");
  const requestConfig = { url };
  if (idType === ProviderType.AUTH_MISA_ID) {
    requestConfig.params = { provider: ProviderType.AUTH_MISA_ID };
  }

  const res = await httpClient.getAsync(requestConfig);
  if (res && !res.error) {
    await cache.set(cacheKey, undefined, res);
  }
  return res;
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    loginProvider: null,
    /** Provider đăng nhập lần cuối — cache persist, không xóa khi logout */
    lastLoginProvider: null,
    amplSid: "",
    avatarUrl: "",
    /** Access token JWT — thay thế Vuex context.Token */
    accessToken: "",
    /** Refresh token */
    refreshToken: "",
    user: {
      userId: "",
      fullName: "",
      email: "",
      misaId: "",
    },
    merchantInfo: {
      merchantCode: "",
      tenantId: "",
      tenantName: "",
      taxCode: "",
    },
    /** Danh sách role của user */
    roles: [],
  }),

  getters: {
    isAmplLogin: (state) => state.loginProvider === ProviderType.AMIS,
    isMisaIdLogin: (state) =>
      state.loginProvider === ProviderType.MISA_ID ||
      state.loginProvider === ProviderType.AUTH_MISA_ID,
    hasAvatar: (state) => !!state.avatarUrl,
    displayAvatarSrc: (state) => (state.hasAvatar ? state.avatarUrl : null),
    /** Có accessToken hợp lệ */
    hasToken: (state) => !!state.accessToken,
    /** Authorization header value: "Bearer <token>" */
    authorizationHeader: (state) => (state.accessToken ? `Bearer ${state.accessToken}` : ""),
    /** Provider đăng nhập lần cuối — dùng để điều hướng login đúng provider */
    lastProvider: (state) => state.lastLoginProvider,
    /** Có quyền truy cập Settings */
    canShowSettings: (state) => SETTINGS_ROLES.some((role) => state.roles.includes(role)),
  },

  actions: {
    /**
     * Lưu provider đăng nhập lần cuối — persist qua logout, dùng cho MisaID.vue điều hướng
     * @param {string} provider - ProviderType.AMIS hoặc ProviderType.MISA_ID
     */
    async setLastProvider(provider) {
      this.lastLoginProvider = provider;
      const cache = getCache();
      await cache.set(CacheKeys.LAST_LOGIN_PROVIDER, undefined, provider);
    },

    /**
     * Khởi tạo auth state từ MISA Cache — gọi khi app start (sau khi cache đã init).
     */
    async init() {
      const cache = getCache();

      const lp = await cache.get(CacheKeys.LOGIN_PROVIDER);
      if (Object.values(ProviderType).includes(lp)) {
        this.loginProvider = lp;
      }

      if (lp === ProviderType.AMIS) {
        this.amplSid = (await cache.get(CacheKeys.AMPL_SID)) || "";
      }

      // Token KHÔNG cache — mỗi lần reload trang, API verify sẽ set lại token

      // Khôi phục provider lần cuối (để điều hướng đúng provider khi vào lại app)
      const lastLp = await cache.get(CacheKeys.LAST_LOGIN_PROVIDER);
      if (Object.values(ProviderType).includes(lastLp)) {
        this.lastLoginProvider = lastLp;
      }

      const user = (await cache.get(CacheKeys.USER)) || {};
      this.avatarUrl = user.avatarUrl || "";
      this.user = {
        userId: user.userId || "",
        fullName: user.fullName || "",
        email: user.email || "",
        misaId: user.misaId || "",
      };

      const merchantInfo = (await cache.get(CacheKeys.MERCHANT_INFO)) || {};
      this.merchantInfo = {
        merchantCode: merchantInfo.merchantCode || "",
        tenantId: merchantInfo.tenantId || "",
        tenantName: merchantInfo.tenantName || "",
        taxCode: merchantInfo.taxCode || "",
      };

      await this.loadRoles();
    },

    /**
     * Nạp danh sách role người dùng từ MISA Cache, fallback localStorage.
     */
    async loadRoles() {
      const cache = getCache();
      try {
        const roles = await cache.get(CacheKeys.ROLE_LOCAL);
        this.roles = roles || JSON.parse(localStorage.getItem("RoleLocal") || "[]");
      } catch {
        this.roles = [];
      }
    },

    /**
     * Lưu accessToken + refreshToken vào state và cache.
     * Gọi từ authService sau khi login/verify/refresh thành công.
     * @param {{ accessToken: string, refreshToken?: string }} tokens
     */
    async setTokens({ accessToken, refreshToken } = {}) {
      this.accessToken = accessToken || "";
      this.refreshToken = refreshToken || "";
      // Token không cache — mỗi lần reload, API verify sẽ set lại
    },

    /**
     * Đăng nhập qua AMIS Platform callback.
     */
    async loginViaAmpl({ user, merchantInfo, avatarUrl, sid } = {}) {
      const cache = getCache();

      this.loginProvider = ProviderType.AMIS;
      this.lastLoginProvider = ProviderType.AMIS;
      this.amplSid = sid || "";
      this.avatarUrl = avatarUrl || "";
      if (user) this.user = { ...this.user, ...user };
      if (merchantInfo) this.merchantInfo = { ...this.merchantInfo, ...merchantInfo };

      await cache.set(CacheKeys.LOGIN_PROVIDER, undefined, ProviderType.AMIS);
      await cache.set(CacheKeys.LAST_LOGIN_PROVIDER, undefined, ProviderType.AMIS);
      await cache.set(CacheKeys.AMPL_SID, undefined, sid || "");
      await cache.set(CacheKeys.USER, undefined, {
        ...user,
        avatarUrl: avatarUrl || "",
      });
      await cache.set(CacheKeys.MERCHANT_INFO, undefined, merchantInfo || {});

      Socket.removeSocketToken();
    },

    /**
     * Đánh dấu provider là MISA_ID sau khi đăng nhập MISA ID thành công.
     */
    async loginViaMisaId({ avatarUrl } = {}) {
      const cache = getCache();

      this.loginProvider = ProviderType.MISA_ID;
      this.lastLoginProvider = ProviderType.MISA_ID;
      this.amplSid = "";
      this.avatarUrl = avatarUrl || "";

      await cache.set(CacheKeys.LOGIN_PROVIDER, undefined, ProviderType.MISA_ID);
      await cache.set(CacheKeys.LAST_LOGIN_PROVIDER, undefined, ProviderType.MISA_ID);
      await cache.remove(CacheKeys.AMPL_SID);

      const userData = (await cache.get(CacheKeys.USER)) || {};
      userData.avatarUrl = avatarUrl || "";
      await cache.set(CacheKeys.USER, undefined, userData);
    },

    /**
     * Đăng xuất — xóa auth state, cache, localStorage, gọi API logout, redirect.
     * Hỗ trợ cả ProviderType.MISA_ID và ProviderType.AMIS provider.
     * @param {Object} opts - { router, skipRedirect }
     *   Nếu skipRedirect=true → chỉ cleanup, không redirect. Trả về { loginUrl } để caller tự redirect.
     */
    async logout({ router, skipRedirect } = {}) {
      const cache = getCache();
      const provider = this.loginProvider;
      let loginUrl = "";

      try {
        useLoadingStore().show();

        // Xóa Pinia state
        this.loginProvider = null;
        this.amplSid = "";
        this.avatarUrl = "";
        this.accessToken = "";
        this.refreshToken = "";
        this.user = {};
        this.merchantInfo = {};
        this.roles = [];

        // Xóa cache keys
        await cache.remove(CacheKeys.LOGIN_PROVIDER);
        await cache.remove(CacheKeys.AMPL_SID);
        await cache.remove(CacheKeys.USER);
        await cache.remove(CacheKeys.MERCHANT_INFO);
        await cache.remove(CacheKeys.MISA_CURRENT_USER);

        // Gọi API logout backend
        let resLogout = null;
        const context = vuexStore.state[ModuleContext];
        resLogout = await vuexStore.dispatch(`${ModuleAuth}/logOut`, {
          UserName: context.User.username,
        });

        // Dựng OIDC external logout URL — đăng xuất khỏi Identity Provider (MISA ID / AMIS)
        const loginProvider =
          provider || localStorage.getItem("loginProvider") || ProviderType.AUTH_MISA_ID;
        let config = null;
        if (loginProvider != ProviderType.AMIS) {
          config = await getAuthConfig(loginProvider);
        }
        if (config) {
          loginUrl = `${config.authority}Account/LogOut?postLogoutRedirectUri=${config.redirect_uri}/login&clientId=${config.client_id}`;
        } else {
          loginUrl = resLogout?.data?.loginUrl || "";
        }
      } catch (e) {
        console.error("Logout error:", e);
      } finally {
        clearSessionStorage();
        useLoadingStore().hide();
        if (skipRedirect) {
          return { loginUrl };
        }
        if (loginUrl) {
          window.location.href = loginUrl;
          return;
        }
        router.push({ name: RouteName.Login });
      }
    },

    /**
     * Khôi phục auth state từ MISA Cache khi app reload.
     */
    async restoreSession() {
      await this.init();
    },
  },
});
